import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { useSectionAccess } from '../hooks/useSectionAccess';
import { getPendingAccessRemoval, processAccessRemoval } from '../api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  ShieldOff, Users, Clock, CheckCircle, AlertTriangle, RefreshCw, 
  UserX, Calendar, Shield, Play
} from 'lucide-react';
import { toast } from 'sonner';

const AccessRemoval = ({ user, setUser }) => {
  const [data, setData] = useState({
    due_now: [],
    pending: [],
    already_removed: []
  });
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await getPendingAccessRemoval();
      setData(response.data);
    } catch (error) {
      console.error(error);
      toast.error('Failed to fetch access removal data');
    } finally {
      setLoading(false);
    }
  };

  const handleProcessRemoval = async () => {
    if (data.due_now.length === 0) {
      toast.info('No employees due for access removal');
      return;
    }

    try {
      setProcessing(true);
      const response = await processAccessRemoval();
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process access removal');
    } finally {
      setProcessing(false);
    }
  };

  // Check access via Permissions module
  const hasAccess = useSectionAccess('/access-removal');
  if (hasAccess === false) {
    return (
      <Layout user={user} setUser={setUser}>
        <div className="flex flex-col items-center justify-center h-96">
          <Shield className="h-16 w-16 text-red-400 mb-4" />
          <h2 className="text-xl font-semibold text-slate-700">Access Denied</h2>
          <p className="text-slate-500 mt-2">You don't have permission to manage access removal.</p>
        </div>
      </Layout>
    );
  }

  if (loading) {
    return (
      <Layout user={user} setUser={setUser}>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} setUser={setUser}>
      <div className="space-y-6" data-testid="access-removal-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Access Removal Management</h1>
            <p className="text-slate-500 text-sm">
              Automated access removal 15 days after FnF settlement release
            </p>
          </div>
          <div className="flex gap-2">
            <Button onClick={fetchData} variant="outline" className="gap-2">
              <RefreshCw size={16} />
              Refresh
            </Button>
            {data.due_now.length > 0 && (
              <Button 
                onClick={handleProcessRemoval} 
                className="bg-red-600 hover:bg-red-700 gap-2"
                disabled={processing}
              >
                {processing ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Processing...
                  </>
                ) : (
                  <>
                    <Play size={16} />
                    Process Removal ({data.due_now.length})
                  </>
                )}
              </Button>
            )}
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-red-200 rounded-lg flex items-center justify-center">
                  <AlertTriangle className="text-red-600" size={24} />
                </div>
                <div>
                  <p className="text-sm text-red-600">Due for Removal</p>
                  <p className="text-3xl font-bold text-red-800">{data.due_now.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-amber-200 rounded-lg flex items-center justify-center">
                  <Clock className="text-amber-600" size={24} />
                </div>
                <div>
                  <p className="text-sm text-amber-600">Pending (Within 15 Days)</p>
                  <p className="text-3xl font-bold text-amber-800">{data.pending.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-emerald-200 rounded-lg flex items-center justify-center">
                  <CheckCircle className="text-emerald-600" size={24} />
                </div>
                <div>
                  <p className="text-sm text-emerald-600">Already Removed</p>
                  <p className="text-3xl font-bold text-emerald-800">{data.already_removed.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Due for Removal Now */}
        {data.due_now.length > 0 && (
          <Card className="border-red-200">
            <CardHeader className="bg-red-50 border-b border-red-200">
              <CardTitle className="text-lg flex items-center gap-2 text-red-800">
                <UserX className="h-5 w-5" />
                Due for Access Removal (15+ Days Since FnF)
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-red-50/50">
                    <tr>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Employee</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">FnF Released</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Scheduled Removal</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.due_now.map((emp, idx) => (
                      <tr key={idx} className="border-b border-slate-100 hover:bg-red-50/30">
                        <td className="py-3 px-4">
                          <p className="font-medium text-slate-900">{emp.name}</p>
                          <p className="text-sm text-slate-500">{emp.employee_id}</p>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <Calendar size={14} className="text-slate-400" />
                            <span>{emp.fnf_released_date}</span>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <span className="text-slate-600">{emp.scheduled_removal_date}</span>
                        </td>
                        <td className="py-3 px-4">
                          <Badge className="bg-red-100 text-red-700 border-red-200">
                            Overdue by {Math.abs(emp.days_remaining)} days
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Pending Removal */}
        {data.pending.length > 0 && (
          <Card className="border-amber-200">
            <CardHeader className="bg-amber-50 border-b border-amber-200">
              <CardTitle className="text-lg flex items-center gap-2 text-amber-800">
                <Clock className="h-5 w-5" />
                Pending Access Removal (Within 15 Days)
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-amber-50/50">
                    <tr>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Employee</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">FnF Released</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Scheduled Removal</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Days Remaining</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.pending.map((emp, idx) => (
                      <tr key={idx} className="border-b border-slate-100 hover:bg-amber-50/30">
                        <td className="py-3 px-4">
                          <p className="font-medium text-slate-900">{emp.name}</p>
                          <p className="text-sm text-slate-500">{emp.employee_id}</p>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <Calendar size={14} className="text-slate-400" />
                            <span>{emp.fnf_released_date}</span>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <span className="text-slate-600">{emp.scheduled_removal_date}</span>
                        </td>
                        <td className="py-3 px-4">
                          <Badge className="bg-amber-100 text-amber-700 border-amber-200">
                            {emp.days_remaining} days left
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Already Removed */}
        {data.already_removed.length > 0 && (
          <Card className="border-emerald-200">
            <CardHeader className="bg-emerald-50 border-b border-emerald-200">
              <CardTitle className="text-lg flex items-center gap-2 text-emerald-800">
                <CheckCircle className="h-5 w-5" />
                Access Already Removed
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-emerald-50/50">
                    <tr>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Employee</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">FnF Released</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Access Removed At</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.already_removed.slice(0, 20).map((emp, idx) => (
                      <tr key={idx} className="border-b border-slate-100 hover:bg-emerald-50/30">
                        <td className="py-3 px-4">
                          <p className="font-medium text-slate-900">{emp.name}</p>
                          <p className="text-sm text-slate-500">{emp.employee_id}</p>
                        </td>
                        <td className="py-3 px-4">
                          <span>{emp.fnf_released_date?.split('T')[0] || '-'}</span>
                        </td>
                        <td className="py-3 px-4">
                          <span>{emp.access_removed_at?.split('T')[0] || '-'}</span>
                        </td>
                        <td className="py-3 px-4">
                          <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">
                            <ShieldOff size={12} className="mr-1" />
                            Access Disabled
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Empty State */}
        {data.due_now.length === 0 && data.pending.length === 0 && data.already_removed.length === 0 && (
          <Card>
            <CardContent className="p-12 text-center">
              <Users className="h-16 w-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-700">No Access Removal Data</h3>
              <p className="text-slate-500 mt-2">
                No employees with completed FnF settlements found. Access removal will be scheduled automatically when FnF is processed.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Info Card */}
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <ShieldOff className="text-blue-600" size={18} />
              </div>
              <div>
                <h4 className="font-medium text-blue-800">Automated Access Removal Policy</h4>
                <p className="text-sm text-blue-700 mt-1">
                  Employee system access is automatically disabled 15 days after their Full & Final (FnF) settlement is released. 
                  This ensures proper offboarding while giving employees time to access any personal documents or information they may need.
                </p>
                <p className="text-sm text-blue-600 mt-2">
                  Click "Process Removal" to manually trigger the access removal for all overdue employees.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default AccessRemoval;
